import { ChangeEvent, ReactNode, useState, memo } from "react";
import { useOutletContext } from "react-router-dom";
import { StoreData } from "../../types/StoreData";
import { IDictionary } from "../../types/PartialProduct";
import { BTCPaymentInfo } from "../../types/BTCTypes";
import BTCPayment from "./BTCPayment";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
  SelectTrigger,
  Input,
  Button,
  Label,
} from "@tides/base-library";
import { Spinner } from "../Spinner";

interface Props {
  accepted_crypto: string[];
  product_id: string;
  email?: string;
  price: string;
}

type HeaderOptions = {
  price?: string;
};

type State = {
  processing: boolean;
  email?: string;
  selected_crypto?: string;
  paying?: boolean;
  payment_info?: BTCPaymentInfo;
};

const Header = memo(function Header(options?: HeaderOptions): ReactNode {
  const { shop_name } = useOutletContext<StoreData>();

  if (options?.price) {
    return (
      <article className="flex flex-col">
        <label className="text-primary text-center">{shop_name}</label>

        <p className="float-left text-center text-xl font-extrabold">
          {options.price}
        </p>
      </article>
    );
  }

  return (
    <article className="text-foreground flex flex-col">
      <label className="form-label mb-2.5 text-center text-xl font-extrabold">
        Choose Cryptocurrency
      </label>
    </article>
  );
});

export default function BTCPayProduct({
  accepted_crypto,
  product_id,
  email,
  price,
}: Props): ReactNode {
  const [state, setState] = useState<State>({
    processing: false,
    paying: false,
    selected_crypto: accepted_crypto[0],
  });

  const items = Object.fromEntries(
    accepted_crypto.map((c) => [c, c])
  ) as IDictionary<string>;

  const handleBTCChange = (value: string) => {
    setState({
      ...state,
      selected_crypto: value,
    });
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      email: event.target.value,
    });
  };

  const handleOnPay = () => {
    setState({
      ...state,
      processing: true,
    });

    fetch(
      `/api/pay/${product_id}/generate-invoice?cryptoCode=${state.selected_crypto!}&email=${state.email!}`,
      {
        method: "POST",
      }
    )
      .then(async (resp) => {
        if (resp.status >= 400) {
          return;
        }

        const result = (await resp.json()) as BTCPaymentInfo;

        setState((currentState) => {
          return {
            ...currentState,
            processing: false,
            paying: true,
            payment_info: result,
          };
        });
      })
      .catch((err: string) => {
        console.error(err);
        setState({
          ...state,
          processing: false,
        });
      });
  };
  return (
    <>
      {state.paying ? (
        <BTCPayment price={price} info={state.payment_info!} />
      ) : (
        <>
          <Header />
          <form className="text-foreground me-4 mr-auto ms-4 mt-0 flex flex-col gap-2 rounded-md px-4">
            <article className="min-w-sm grid w-full items-center gap-1.5">
              <Label>Crypto</Label>
              <Select
                onValueChange={handleBTCChange}
                defaultValue={Object.values(items)[0]}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Crypto" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(items).map((key) => (
                    <SelectItem value={key} key={key}>
                      {items[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </article>
            <article className="flex flex-col gap-2.5">
              <Label>Email</Label>
              <Input
                type="email"
                defaultValue={email}
                onChange={handleEmailChange}
              />
            </article>

            {state.processing ? (
              <Spinner text="Loading..." />
            ) : (
              <Button
                className="text-white"
                type="button"
                onClick={handleOnPay}
              >
                Continue
              </Button>
            )}
          </form>
        </>
      )}
    </>
  );
}
